import { createStrings } from '@utils/local';
import { file, formatDate, isValidDate } from '@utils/types';
import dayjs from 'dayjs';
import { FieldMetaProps, useField, useFormikContext } from 'formik';
import { fold } from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import * as t from 'io-ts';
import { toAscii } from 'japanese-string-utils';
import { merge } from 'lodash-es';
import * as h from 'tyrann-io';

export const formStrings = createStrings({
  ja: {
    notEmpty: (name: string) => `${name}は必須です。`,
    thisNotEmpty: `この欄の記入は必須です。`,
    tooLong: (name: string, n: number) => `${name}は${n}文字以内でご入力ください。`,
    tooShort: (name: string, n: number) => `${name}は${n}文字以上でご入力ください。`,
    invalid: (name: string) => `有効な${name}をご入力ください。`,
    englishPrompt: '英字でご入力ください',
    email: 'メールアドレス',
    password: 'パスワード',
    login: 'ログイン',
    shouldSelect: '選択肢を選んでください。',
    submit: '送信',
    search: '検索',
    suru: 'する',
    confirm: '確認',
    no: 'いいえ',
    yes: 'はい',
    notUse: '利用しない',
    use: '利用する',
    cancel: 'キャンセル',
    addNew: '新規追加',
    update: '更新',
    change: '変更',
    edit: '編集',
    error: 'エラー',
    reset: 'リセット',
    filter: '絞り込み',
    none: 'なし',
    exist: 'あり',
    remove: '解除',
    promptLeaving:
      'このページにはまだ保存していないデータがありますが、本当にほかのページへ移動しますか？',
    promptClosing: 'このフォームはまだ保存してません。本当に閉じますか？',
    hankakuPrompt: '半角数字で入力してください',
    repeatPrompt: (name: string) => `${name}を再度ご入力ください`,
    required: '【必須】',
    notRequired: '【任意】',
    unknownError:
      '申し訳ございません。不明なエラーが発生しました。繰り返す場合は左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。',
    create: '作成',
    export: (subject: string) => `${subject}をエクスポートする`,
    thisField: 'この欄',
    prev: '戻る',
    next: '次へ',
    delete: '削除',
    save: '保存',
    selectFiles: 'ファイルを選択',
    from: 'から',
    until: 'まで',
    add: '追加',
    choose: '選択',
    chosen: '選択済み',
    ok: 'OK',
    pageSize: '表示件数',
    notFound: '見つかりませんでした。',
    downloadError: 'ダウンロードできませんでした。',
    comma: '、',
    unlock: 'ロック解除',
    noResult: (title: string) => `お探しの${title}は見つかりません。ほかの条件で検索してください。`,
    fixMessage: '誤りを修正してもう一度ご提出ください。',
    exportCsv: 'Export CSV',
    success: '成功',
    submissionSuccess: '提出に成功しました。',
    saved: '保存しました。',
    temporarilySave: '一時保存',
    optional: '任意',
    upload: 'アップロード',
    changeReason: '変更理由',
    tagSearchInputHint: '*入力が完了したら「Enter」キーを押すと、他の検索条件を追加できる',
  },
});

export type ValidateFunction = (o: any) => any;

export interface CreateVaidateOptions {
  omitUnion?: boolean;
  fields?: Record<string, ValidateFunction>;
}

export const createValidate =
  (type: t.Type<any>, options: CreateVaidateOptions = {}) =>
  (values: any) => {
    const { omitUnion = false } = options;

    const v = type.decode(values);
    const errors = pipe(
      v,
      fold(
        (errs) =>
          errs.map(
            (error) =>
              [
                error.context
                  // eslint-disable-next-line no-underscore-dangle
                  .filter(
                    (trace) => omitUnion === false || (trace.type as any)._tag !== 'UnionType',
                  )
                  // union creates extra context that misleads us
                  .map(({ key }) => key)
                  .filter((key) => key),
                error.message,
              ] as const,
          ),
        () => [],
      ),
    );

    const o: any = {};
    for (const [keys, message] of errors) {
      let cur = o;
      let i = 0;
      for (const key of keys) {
        cur[key] = cur[key] === undefined ? {} : cur[key];
        if (i < keys.length - 1) {
          i++;
          cur = cur[key];
        }
      }
      cur[keys[keys.length - 1]] = message;
    }

    if (options.fields) {
      for (const [field, validate] of Object.entries(options.fields)) {
        if (!Array.isArray(values[field])) {
          const fieldErrors = validate(values[field]);

          if (fieldErrors && Object.keys(fieldErrors).length) {
            o[field] = merge(o[field], fieldErrors);
          }
        } else {
          for (let i = 0; i < values[field].length; i++) {
            const fieldErrors = validate(values[field][i]);

            if (fieldErrors && Object.keys(fieldErrors).length) {
              o[field] = o[field] ?? {};

              o[field][i] = merge(o[field][i], fieldErrors);
            }
          }
        }
      }
    }

    return o;
  };

export function compactObject(data: any): any {
  if (typeof data !== 'object') {
    return data;
  }

  return Object.keys(data).reduce(function (accumulator, key) {
    const isObject = typeof data[key] === 'object' && data[key] !== null;
    const value = isObject ? compactObject(data[key]) : data[key];
    const isEmptyObject = isObject && !Object.keys(value).length;
    if (value == null || isEmptyObject) {
      return accumulator;
    }

    return Object.assign(accumulator, { [key]: value });
  }, {});
}

export const mergeErrors = (e1: Record<string, any>, e2: Record<string, any>) => {
  return compactObject(merge(e1, e2));
};

export const newPasswordField = (name: string) => {
  return (
    h
      .string()
      .min(1, formStrings.notEmpty(name))
      .min(8, formStrings.tooShort(name, 8))
      // .matches(/(<=.*[0-9]).*[a-zA-Z]/g, `${name}は数字・英文字を最低一文字で入力してください。`)
      .max(128, formStrings.tooLong(name, 128))
  );
};

export const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
export const optionalEmailRegex = /^$|^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
export const phoneRegex = /([0-9\s-]{7,})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;

export const newEmailField = (name: string) => {
  return h
    .string()
    .cast((s) => (typeof s === 'string' ? toAscii(s) : (s as string)))
    .min(1, formStrings.notEmpty(name))
    .max(64, formStrings.tooLong(name, 64))
    .matches(emailRegex, `${name}は有効なメールアドレスを入力してください。`);
};

export const optionalEmailField = (name: string) => {
  return h
    .string()
    .cast((s) => (typeof s === 'string' ? toAscii(s) : (s as string)))
    .max(64, formStrings.tooLong(name, 64))
    .matches(optionalEmailRegex, `${name}は有効なメールアドレスを入力してください。`);
};

export const requiredStringField = (
  name: string,
  min: number | undefined = 1,
  max: number | undefined = 128,
) => {
  let schema = h
    .string()
    .cast((s) => (typeof s === 'string' ? s.trim() : (s as string)))
    .min(1, formStrings.notEmpty(name))
    .withLabel(name);

  if (max !== undefined) {
    schema = schema.max(max, formStrings.tooLong(name, max));
  }
  if (min !== undefined) {
    schema = schema.min(min, formStrings.tooShort(name, min));
  }
  return schema;
};

export const stringField = (
  name: string,
  min: number | undefined = 0,
  max: number | undefined = 128,
) => {
  let schema = h.string().withLabel(name);

  if (max !== undefined) {
    schema = schema.max(max, formStrings.tooLong(name, max));
  }
  if (min !== undefined) {
    schema = schema.min(min, formStrings.tooShort(name, min));
  }
  return schema;
};

export const unlimitedStringField = (
  name: string,
  min?: number | undefined,
  max?: number | undefined,
) => {
  let schema = h.string().withLabel(name);

  if (max !== undefined) {
    schema = schema.max(max, formStrings.tooLong(name, max));
  }
  if (min !== undefined) {
    schema = schema.min(min, formStrings.tooShort(name, min));
  }
  return schema;
};

export const omittableStringField = (
  name: string,
  min: number | undefined = 0,
  max: number | undefined = 128,
) => {
  let schema = h.omittableString().withLabel(name).cast();

  if (max !== undefined) {
    schema = schema.max(max, formStrings.tooLong(name, max));
  }
  if (min !== undefined) {
    schema = schema.min(min, formStrings.tooShort(name, min));
  }
  return schema;
};

export const stringNumberToThirdDecimalPlaceField = (name: string) => {
  const schema = requiredStringField(name).matches(
    /^\d+(\.\d{1,3})?$/,
    `${name}は数値です。小数第3位まで含ませることができます。`,
  );

  return schema;
};

export const phoneField = (name: string) => {
  return requiredStringField(name).matches(
    phoneRegex,
    `有効な${name}を入力してください。全角の数字を入力しないでください。`,
  );
};

export const faxField = (name: string) => {
  return requiredStringField(name).matches(
    phoneRegex,
    `有効な${name}を入力してください。全角の数字を入力しないでください。`,
  );
};

export const dateField = (name: string) => {
  return requiredStringField(name).refine(
    isValidDate,
    `有効な${name}を入力してください。例：${formatDate(dayjs())}`,
  );
};

export const omittableDateField = (name: string) => {
  return omittableStringField(name).refine(
    (s) => s === undefined || isValidDate(s),
    `有効な${name}を入力してください。例：${formatDate(dayjs())}`,
  );
};

export const requiredPositiveIntegerField = (name?: string) => {
  return h
    .number()
    .cast(`${name ? `${name}は` : ''}半角数字で入力してください。`)
    .positive(`${name ? `${name}は` : ''}正数で入力してください。`)
    .integer(`${name ? `${name}は` : ''}整数で入力してください。`);
};

export const positiveIntegerField = (name?: string) => {
  return h
    .omittableNumber()
    .cast(`${name ? `${name}は` : ''}半角数字で入力してください。`)
    .refine((s) => (s ? s > 0 : true), `${name ? `${name}は` : ''}正数で入力してください。`)
    .refine(
      (s) => (s ? Number.isSafeInteger(s) : true),
      `${name ? `${name}は` : ''}整数で入力してください。`,
    );
};

export const requiredPositiveNumberField = (name?: string) => {
  return h
    .number()
    .cast(`${name ? `${name}は` : ''}半角数字で入力してください。`)
    .positive(`${name ? `${name}は` : ''}正数で入力してください。`);
};

export const requiredNumberField = (name?: string) => {
  return h.number().castInput(`${name ? `${name}は` : ''}半角数字で入力してください。`);
};

export const positiveNumberField = (name?: string) => {
  return h
    .omittableNumber()
    .cast(`${name ? `${name}は` : ''}半角数字で入力してください。`)
    .refine((s) => (s ? s > 0 : true), `${name ? `${name}は` : ''}正数で入力してください。`);
};

export const yesNoField = (name: string, reasonWhen: 'Y' | 'N' | undefined = undefined) => {
  return h
    .type({
      answer: h.string().required('選択肢を選んでください。'),
      reason: h.string().max(1024, `${name}は 1024 文字以内で入力してください。`),
    })
    .refine(
      (value) =>
        !(
          reasonWhen !== undefined &&
          value.answer === reasonWhen &&
          value.reason.trim().length === 0
        ),
      '詳細を説明してください。',
    );
};

export const goodsField = () => {
  return t.type({
    territory: h.string(),
    types: h.string(),
    amount: h
      .number()
      .cast('有効な件数を入力してください。全角の数字を入力しないでください。')
      .min(0, '正整数を入力してください。')
      .integer('正整数を入力してください。'),
    illegal: h.string(),
  });
};

export const foreignKeyField = (name: string, options?: { required: boolean }) => {
  const { required = true } = options ?? {};

  let type = h.omittableNumber().cast();

  if (required) {
    type = type.refine((s) => !!s, `${name}を選択してください`);
  }

  return type;
};

export const remoteFileField = (name: string, required: boolean = true) => {
  return h
    .type({
      file: h.omittable(file(formStrings.notEmpty(name))),
      remote: h.omittable(t.string),
    })
    .refine(
      (value) => (required ? !!value.file || !!value.remote : true),
      formStrings.notEmpty(name),
    );
};

export type RemoteFileField = t.TypeOf<ReturnType<typeof remoteFileField>>;

export interface FieldOptions {
  omittable: boolean;
  multiline: boolean;
  choose: boolean;
  options?: any[]; // TextInputOptions, omitted because of tsc performance
  placeholder?: string;
  tips?: string;
}

export interface Field<A> {
  type: t.Type<A>;
  name: string;
  label: string;
  options: FieldOptions;
}

export interface YesNoValue {
  answer: string;
  reason: string;
}

export interface YesNoField extends Field<YesNoValue> {
  yes: string;
  no: string;
  reasonWhen: string | undefined;
  placeholder: string;
  prompt?: string;
}

export type GoodsValue = t.TypeOf<ReturnType<typeof goodsField>>;
export interface GoodsField extends Field<GoodsValue> {
  legalQuestion: string;
}

const defaultFieldOptions: FieldOptions = {
  omittable: false,
  multiline: false,
  choose: false,
};

export const createField = <A>(
  getType: (label: string) => t.Type<A>,
  name: string,
  intlLabel: Record<string, string>,
  customOptions: Partial<FieldOptions> = {},
): Field<A> => {
  const options: FieldOptions = {
    ...defaultFieldOptions,
    ...customOptions,
  };

  return {
    type: getType(intlLabel.ja), // TODO: Get the language
    name,
    label: intlLabel.ja,
    options,
  };
};

export const createYesNoField = (
  name: string,
  label: string,
  yes: string,
  no: string,
  reasonWhen: 'Y' | 'N' | undefined,
  placeholder: string = '',
  prompt: string | undefined = undefined,
  customOptions: Partial<FieldOptions> = {},
): YesNoField => {
  const options: FieldOptions = {
    ...defaultFieldOptions,
    ...customOptions,
  };

  return {
    type: yesNoField(name, reasonWhen),
    reasonWhen,
    name,
    label,
    yes,
    no,
    placeholder,
    prompt,
    options,
  };
};

export const createGoodsField = (
  name: string,
  label: string,
  legalQuestion: string,
  customOptions: Partial<FieldOptions> = {},
): GoodsField => {
  const options: FieldOptions = {
    ...defaultFieldOptions,
    ...customOptions,
  };

  return {
    name,
    label,
    type: goodsField(),
    legalQuestion,
    options,
  };
};

export interface TransportationInputProps {
  field: TransportationField;
  disabled: boolean;
  iconClick?: () => void;
}

export interface TransportationField extends Field<string> {
  ddp: string;
  exw: string;
  ddu: string;
  fca: string;
  placeholder: string;
}

export const createTransportationField = (
  name: string,
  label: string,
  ddp: string,
  exw: string,
  ddu: string,
  fca: string,
  placeholder: string = '',
  customOptions: Partial<FieldOptions> = {},
): TransportationField => {
  const options: FieldOptions = {
    ...defaultFieldOptions,
    ...customOptions,
  };

  return {
    type: h.string().min(1, formStrings.shouldSelect),
    name,
    label,
    ddp,
    exw,
    ddu,
    fca,
    placeholder,
    options,
  };
};

export const useShouldDisplayError = (meta: FieldMetaProps<any>) => {
  const context = useFormikContext();
  return (meta.touched || context.submitCount > 0) && meta.error;
};

export const useErrorMessage = (name: string) => {
  const [, meta] = useField(name);

  const shouldDisplay = useShouldDisplayError(meta);

  if (shouldDisplay) {
    return meta.error;
  }
  return undefined;
};

export const joinKeyPath = (root?: string | number, ...paths: (string | number)[]) => {
  return [String(root ?? ''), ...paths.map(String)].filter(Boolean).join('.');
};
